import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import ar from '../../locales/ar';
import { formatDate } from '../../utils/dateFormatter';
import { formatCount } from '../../utils/numberFormatter';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import {
  useEditReview,
  useSendReview,
  useMarkAlreadyReviewed,
  useEditThankYou,
  useSendThankYou,
  useConfirmThankYouSent
} from '../../hooks/useGuestReviews';
import {
  SCORE_ORDER,
  SCORE_LABELS,
  REVIEW_STATUS,
  THANK_YOU_STATUS,
  FAILURE_TEXT,
  CLEANER_ANSWER,
  TEXT
} from '../../config/guestReviewLabels';
import { StatusChip, ChoiceButton, ErrorLine, daysLeftLabel, useRunner, textDir } from './shared';
import TaskLinkButton from './TaskLinkButton';

/**
 * One departed stay on the Reviews page (tasks/post-checkout-review-and-replies.md L18): our review of
 * the guest and the thank-you — two approvals on one card, one click each by default. «تعديل» opens the
 * text inline on the same card (L46); a recommendation that contradicts the cleaner asks for a reason
 * (L49). Everything shown is the server's; after any action the list is refetched, never patched locally.
 */

const SENDABLE = ['needs_approval', 'failed'];
const reasonLabel = (code) => (code && ar.tasks.guestAssessment.reasons[code]) || null;
const onlyScores = (scores) => Object.fromEntries(Object.entries(scores || {}).filter(([, v]) => Number.isInteger(v)));

function ReviewBlock({ item, sendOn }) {
  const review = item.review;
  const answer = (item.cleanerAnswer && item.cleanerAnswer.answer) || null;
  const fresh = () => ({
    scores: { ...review.scores },
    recommended: review.recommended,
    publicText: review.publicText || '',
    overrideReason: ''
  });
  // Still incomplete («لا», or no answer): the card opens on the choices (L12). The server says what is
  // missing — and stops saying it once ops has filled it in.
  const incomplete = Boolean(review.missing) && review.status === 'needs_approval';
  const [editing, setEditing] = useState(incomplete);
  const [confirmingAlready, setConfirmingAlready] = useState(false);
  const [draft, setDraft] = useState(fresh);
  const edit = useEditReview();
  const send = useSendReview();
  const already = useMarkAlreadyReviewed();
  const { error, run } = useRunner();

  const busy = edit.isPending || send.isPending || already.isPending || review.status === 'sending';
  const contradicts = (answer === 'yes' && draft.recommended === false) || (answer === 'no' && draft.recommended === true);
  // The server's rule (L49): a reason is asked only when the recommendation CHANGES against the cleaner.
  const needsReason = contradicts && draft.recommended !== review.recommended;
  const open = SENDABLE.includes(review.status) || review.status === 'unknown';
  const canSend = sendOn && review.slotReady && SENDABLE.includes(review.status);

  const save = () => run(edit, {
    id: item.id,
    changes: {
      scores: onlyScores(draft.scores),
      recommended: typeof draft.recommended === 'boolean' ? draft.recommended : undefined,
      publicText: draft.publicText,
      overrideReason: needsReason ? draft.overrideReason : undefined
    }
  }, () => setEditing(false));

  return (
    <section className="gr-block" aria-label={TEXT.reviewTitle}>
      <div className="gr-block-head">
        <h3 className="gr-block-title">{TEXT.reviewTitle}</h3>
        <StatusChip status={review.status} labels={REVIEW_STATUS} />
      </div>

      {incomplete && <div className="gr-attention">{TEXT.needsAttention}</div>}

      {editing ? (
        <div className="gr-edit">
          {SCORE_ORDER.map(category => (
            <div key={category} className="gr-score-row">
              <span className="gr-score-label">{SCORE_LABELS[category]}</span>
              <div className="gr-choices" role="group" aria-label={SCORE_LABELS[category]}>
                {[1, 2, 3, 4, 5].map(n => (
                  <ChoiceButton
                    key={n}
                    active={draft.scores[category] === n}
                    disabled={busy}
                    onClick={() => setDraft(d => ({ ...d, scores: { ...d.scores, [category]: n } }))}
                  >
                    {formatCount(n)}
                  </ChoiceButton>
                ))}
              </div>
            </div>
          ))}
          <div className="gr-choices" role="group" aria-label={TEXT.recommended}>
            <ChoiceButton active={draft.recommended === true} disabled={busy} onClick={() => setDraft(d => ({ ...d, recommended: true }))}>
              {TEXT.recommended}
            </ChoiceButton>
            <ChoiceButton active={draft.recommended === false} disabled={busy} onClick={() => setDraft(d => ({ ...d, recommended: false }))}>
              {TEXT.notRecommended}
            </ChoiceButton>
          </div>
          <Form.Control
            as="textarea"
            rows={2}
            className="touch-input gr-textarea"
            value={draft.publicText}
            maxLength={1000}
            disabled={busy}
            onChange={e => setDraft(d => ({ ...d, publicText: e.target.value }))}
            placeholder={TEXT.reviewTextPlaceholder}
            aria-label={TEXT.reviewTextPlaceholder}
            dir={textDir(draft.publicText)}
          />
          {needsReason && (
            <Form.Control
              as="textarea"
              rows={2}
              className="touch-input gr-textarea"
              value={draft.overrideReason}
              maxLength={500}
              disabled={busy}
              onChange={e => setDraft(d => ({ ...d, overrideReason: e.target.value }))}
              placeholder={TEXT.overrideReasonPlaceholder}
              aria-label={TEXT.overrideReason}
              dir={textDir(draft.overrideReason)}
            />
          )}
          <ErrorLine message={error} />
          <div className="gr-actions">
            <Button className="touch-btn gr-primary" onClick={save} disabled={busy}>
              {edit.isPending ? <Spinner animation="border" size="sm" /> : TEXT.save}
            </Button>
            <Button variant="light" className="touch-btn" onClick={() => { setDraft(fresh()); setEditing(false); }} disabled={busy}>
              {TEXT.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="gr-scores">
            {SCORE_ORDER.map(category => (
              <span key={category}>
                {SCORE_LABELS[category]} {Number.isInteger(review.scores && review.scores[category]) ? formatCount(review.scores[category]) : '—'}
              </span>
            ))}
            <span className="gr-recommend">
              {review.recommended === true ? TEXT.recommended : review.recommended === false ? TEXT.notRecommended : TEXT.recommendUnset}
            </span>
          </div>
          <blockquote className="gr-text" dir="auto">{review.publicText}</blockquote>
          <ErrorLine message={error} />
          {open && (
            <div className="gr-actions">
              {SENDABLE.includes(review.status) && (
                <Button className="touch-btn gr-primary" onClick={() => run(send, { id: item.id })} disabled={busy || !canSend}>
                  {send.isPending ? <Spinner animation="border" size="sm" /> : TEXT.approveReview}
                </Button>
              )}
              {review.status === 'unknown' && (
                <Button className="touch-btn gr-primary" onClick={() => run(send, { id: item.id, confirmUnknown: true })} disabled={busy || !sendOn}>
                  {TEXT.resendChecked}
                </Button>
              )}
              <Button variant="outline-secondary" className="touch-btn" onClick={() => { setDraft(fresh()); setEditing(true); }} disabled={busy}>
                {TEXT.edit}
              </Button>
              <Button variant="link" className="touch-btn gr-link" onClick={() => setConfirmingAlready(true)} disabled={busy || confirmingAlready}>
                {TEXT.markAlreadyReviewed}
              </Button>
            </div>
          )}
          {/* Final and irreversible — asked once more before it closes the review for good (L41). */}
          {open && confirmingAlready && (
            <div className="gr-confirm" role="group" aria-label={TEXT.markAlreadyReviewed}>
              <p className="gr-confirm-text">{TEXT.alreadyReviewedConfirm}</p>
              <div className="gr-actions">
                <Button className="touch-btn gr-primary" onClick={() => run(already, { id: item.id }, () => setConfirmingAlready(false))} disabled={busy}>
                  {already.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirm}
                </Button>
                <Button variant="light" className="touch-btn" onClick={() => setConfirmingAlready(false)} disabled={busy}>
                  {TEXT.cancel}
                </Button>
              </div>
            </div>
          )}
          {open && sendOn && !review.slotReady && <div className="gr-hint">{TEXT.slotNotReady}</div>}
        </>
      )}

      <div className="gr-details">
        {review.status === 'sending' && <span><Spinner animation="border" size="sm" /> {REVIEW_STATUS.sending}</span>}
        {review.failure && ['failed', 'unknown'].includes(review.status) && (
          <span className="gr-failure">{FAILURE_TEXT[review.failure.code]}</span>
        )}
        {review.approvedByName && ['sending', 'sent', 'published'].includes(review.status) && (
          <span>{TEXT.by} {review.approvedByName}</span>
        )}
        {review.edited && <span>{TEXT.edited}</span>}
        {review.overridden && <span>{TEXT.overridden}</span>}
      </div>
    </section>
  );
}

function ThankYouBlock({ item, sendOn }) {
  const thankYou = item.thankYou;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thankYou.text || '');
  const edit = useEditThankYou();
  const send = useSendThankYou();
  const confirmSent = useConfirmThankYouSent();
  const { error, run } = useRunner();

  const busy = edit.isPending || send.isPending || confirmSent.isPending || thankYou.status === 'sending';
  const open = SENDABLE.includes(thankYou.status) || thankYou.status === 'unknown';

  return (
    <section className="gr-block" aria-label={TEXT.thankYouTitle}>
      <div className="gr-block-head">
        <h3 className="gr-block-title">{TEXT.thankYouTitle}</h3>
        <StatusChip status={thankYou.status} labels={THANK_YOU_STATUS} />
      </div>

      {editing ? (
        <div className="gr-edit">
          <Form.Control
            as="textarea"
            rows={3}
            className="touch-input gr-textarea"
            value={draft}
            maxLength={1000}
            disabled={busy}
            onChange={e => setDraft(e.target.value)}
            placeholder={TEXT.thankYouPlaceholder}
            aria-label={TEXT.thankYouPlaceholder}
            dir={textDir(draft)}
          />
          <ErrorLine message={error} />
          <div className="gr-actions">
            <Button className="touch-btn gr-primary" onClick={() => run(edit, { id: item.id, text: draft }, () => setEditing(false))} disabled={busy}>
              {edit.isPending ? <Spinner animation="border" size="sm" /> : TEXT.save}
            </Button>
            <Button variant="light" className="touch-btn" onClick={() => { setDraft(thankYou.text || ''); setEditing(false); }} disabled={busy}>
              {TEXT.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <blockquote className="gr-text" dir="auto">{thankYou.text}</blockquote>
          <ErrorLine message={error} />
          {open && (
            <div className="gr-actions">
              {SENDABLE.includes(thankYou.status) && (
                <Button className="touch-btn gr-primary" onClick={() => run(send, { id: item.id })} disabled={busy || !sendOn}>
                  {send.isPending ? <Spinner animation="border" size="sm" /> : TEXT.approveThankYou}
                </Button>
              )}
              {thankYou.status === 'unknown' && (
                <>
                  <Button className="touch-btn gr-primary" onClick={() => run(send, { id: item.id, confirmUnknown: true })} disabled={busy || !sendOn}>
                    {TEXT.resendChecked}
                  </Button>
                  <Button variant="outline-secondary" className="touch-btn" onClick={() => run(confirmSent, { id: item.id })} disabled={busy}>
                    {confirmSent.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirmDelivered}
                  </Button>
                </>
              )}
              <Button variant="outline-secondary" className="touch-btn" onClick={() => { setDraft(thankYou.text || ''); setEditing(true); }} disabled={busy}>
                {TEXT.edit}
              </Button>
            </div>
          )}
        </>
      )}

      <div className="gr-details">
        {thankYou.status === 'sending' && <span><Spinner animation="border" size="sm" /> {THANK_YOU_STATUS.sending}</span>}
        {thankYou.failure && ['failed', 'unknown'].includes(thankYou.status) && (
          <span className="gr-failure">{FAILURE_TEXT[thankYou.failure.code]}</span>
        )}
        {thankYou.approvedByName && ['sending', 'sent'].includes(thankYou.status) && (
          <span>{TEXT.by} {thankYou.approvedByName}</span>
        )}
        {thankYou.edited && <span>{TEXT.edited}</span>}
      </div>
    </section>
  );
}

const GuestReviewCard = ({ item, switches, onOpenTask, loadingTaskId }) => {
  const answer = item.cleanerAnswer && item.cleanerAnswer.answer;
  const reason = reasonLabel(item.cleanerAnswer && item.cleanerAnswer.reasonCode);
  const note = item.cleanerAnswer && item.cleanerAnswer.note;
  const days = daysLeftLabel(item.daysLeft);

  return (
    <article className="gr-card">
      <header className="gr-card-head">
        <div className="gr-card-title">
          <span className="gr-unit">{item.unit && item.unit.name}</span>
          {item.unit && item.unit.code && <span className="gr-code" dir="ltr">{item.unit.code}</span>}
          <span className="gr-platform">{getPlatformDisplayName(item.platform)}</span>
        </div>
        {days && <span className={`gr-days ${item.daysLeft <= 2 ? 'gr-days--soon' : ''}`}>{days}</span>}
      </header>

      <div className="gr-details">
        <span>{item.guestName}</span>
        {item.checkoutAt && <span>{TEXT.departed} {formatDate(item.checkoutAt)}</span>}
        <span>
          {TEXT.cleanerSaid}: {answer ? CLEANER_ANSWER[answer] : TEXT.noAnswer}{reason ? ` (${reason})` : ''}
        </span>
      </div>
      {note && <p className="gr-note">{TEXT.cleanerNote}: <span dir="auto">{note}</span></p>}
      <TaskLinkButton
        taskId={item.taskId}
        ticketId={item.ticketId}
        onOpen={onOpenTask}
        loading={loadingTaskId === item.taskId}
      />

      {item.review && <ReviewBlock item={item} sendOn={switches.reviewSend} />}
      {item.thankYou && <ThankYouBlock item={item} sendOn={switches.thankYouSend} />}
    </article>
  );
};

export default GuestReviewCard;
